import { gameConfig } from '../config/gameConfig'

export interface GameResult {
  gameNumber: number
  completed: boolean
  won: boolean
  clubName?: string
  playersRevealed?: number
  livesRemaining?: number // Keep optional for backward compatibility
  completedAt: string
  guessedLetters: string[]
  originallyGuessedLetters?: string[]
  incorrectThemeGuesses: string[]
  gameStatus: 'playing' | 'won' | 'lost' | 'theme-guess'
}

export function getCurrentGameNumber(): number {
  const now = new Date()
  // Convert to local date to avoid timezone issues
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  // Convert game start date to local date - adjusted so today (Sept 20) is game 7
  const gameStartLocal = new Date(2025, 8, 14) // September 14, 2025 (month is 0-indexed)
  
  const timeDiff = today.getTime() - gameStartLocal.getTime()
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
  return Math.max(1, daysDiff + 1)
}

export function getTimeUntilNextGame(): { hours: number; minutes: number; seconds: number } {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000)
  
  const timeDiff = tomorrow.getTime() - now.getTime()
  
  const hours = Math.floor(timeDiff / (1000 * 60 * 60))
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)
  
  return { hours, minutes, seconds }
}

export function getTodayGameKey(): string {
  const now = new Date()
  const dateString = formatDateForStorage(now)
  return `${gameConfig.storagePrefix}-game-${dateString}`
}

export function hasPlayedToday(): boolean {
  const gameKey = getTodayGameKey()
  const gameData = localStorage.getItem(gameKey)
  return gameData !== null
}

export function markGameAsPlayed(gameResult: GameResult): void {
  const gameKey = getTodayGameKey()
  localStorage.setItem(gameKey, JSON.stringify(gameResult))
}

export function getTodayGameResult(): GameResult | null {
  const gameKey = getTodayGameKey()
  const gameData = localStorage.getItem(gameKey)
  return gameData ? JSON.parse(gameData) : null
}

export function getGameKeyForDate(date: Date): string {
  const dateString = formatDateForStorage(date)
  return `${gameConfig.storagePrefix}-game-${dateString}`
}

export function formatDateForStorage(date: Date): string {
  // Format date as YYYY-MM-DD in local timezone (not UTC)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function parseDateFromStorage(dateString: string): Date {
  // Parse YYYY-MM-DD string as local date (not UTC)
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day) // month is 0-indexed in Date constructor
}

export function getGameResultForDate(date: Date): GameResult | null {
  const gameKey = getGameKeyForDate(date)
  const gameData = localStorage.getItem(gameKey)
  return gameData ? JSON.parse(gameData) : null
}

export function getGameNumberForDate(date: Date): number {
  // Convert to local date to avoid timezone issues
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  // Convert game start date to local date - adjusted so today (Sept 20) is game 7
  const gameStartLocal = new Date(2025, 8, 14) // September 14, 2025 (month is 0-indexed)
  
  const timeDiff = targetDate.getTime() - gameStartLocal.getTime()
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
  return Math.max(1, daysDiff + 1)
}

export interface WeeklyGameDay {
  date: Date
  gameNumber: number
  dayLabel: string
  isToday: boolean
  gameResult: GameResult | null
  status: 'unplayed' | 'won' | 'lost'
}

export function getWeeklyGameDays(): WeeklyGameDay[] {
  const today = new Date()
  const weekDays: WeeklyGameDay[] = []
  
  // Generate last 7 days (including today) - today should be day 7 (rightmost)
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    
    const gameNumber = getGameNumberForDate(date)
    const gameResult = getGameResultForDate(date)
    
    // Don't allow future games (games beyond today)
    if (gameNumber > getCurrentGameNumber()) {
      continue
    }
    
    let status: 'unplayed' | 'won' | 'lost' = 'unplayed'
    if (gameResult && gameResult.completed) {
      status = gameResult.won ? 'won' : 'lost'
    }
    
    // Get day label
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const dayLabel = i === 0 ? 'Today' : dayNames[date.getDay()]
    
    weekDays.push({
      date,
      gameNumber,
      dayLabel,
      isToday: i === 0,
      gameResult,
      status
    })
  }
  
  return weekDays
}
